import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Bug, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { useAuth } from '../../auth/AuthContext';
import { useReportContextStore } from '../../store/reportContextStore';
import { submitIssueReport } from '../../utils/reportIssue';

/**
 * Persistent "Report bug/feature" trigger — mounted once at the app root
 * (sibling of <AppRoutes/>) so it survives every route/view change. Docked as
 * a slim edge tab so it never sits on top of other on-screen controls; the
 * form only appears in a slide-in side panel once opened.
 */
export default function ReportIssueButton() {
  const { activeOrganizationId, session } = useAuth();
  const location = useLocation();
  const reportContext = useReportContextStore();

  const [isOpen, setIsOpen] = useState(false);
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [sent, setSent] = useState(false);

  const close = () => {
    if (submitting) return;
    setIsOpen(false);
    setError(null);
    setSent(false);
    setDescription('');
  };

  const onSubmit = async () => {
    if (!description.trim() || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await submitIssueReport({
        organizationId: activeOrganizationId || null,
        description: description.trim(),
        context: {
          pathname: location.pathname,
          userEmail: session?.user?.email || null,
          organizationName: reportContext.organizationName,
          screenName: reportContext.screenName,
          parliamentaryMode: reportContext.parliamentaryMode,
          sessionMode: reportContext.sessionMode,
          quorumRequirement: reportContext.quorumRequirement,
          quorumType: reportContext.quorumType,
          inMeeting: reportContext.inMeeting,
          inElection: reportContext.inElection,
          userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
          timestamp: new Date().toISOString(),
        },
      });
      setSent(true);
    } catch (err) {
      setError(err.message || 'Could not send report');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Discreet edge tab — never occludes page content */}
      <button
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        title="Report a bug or suggest a feature"
        className={`fixed top-1/2 z-[100] flex -translate-y-1/2 flex-col items-center gap-1 rounded-l-lg bg-rose-600 py-3 pl-1.5 pr-1 text-white shadow-lg transition-all duration-300 hover:opacity-100 ${
          isOpen ? 'right-[min(20rem,90vw)] opacity-100' : 'right-0 opacity-60'
        }`}
      >
        {isOpen ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronLeft className="h-3.5 w-3.5" />}
        <Bug className="h-3.5 w-3.5" />
      </button>

      {/* Slide-in side panel */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="report-issue-title"
        aria-hidden={!isOpen}
        className={`fixed right-0 top-0 z-[100] h-full w-80 max-w-[90vw] transform border-l border-pp-line bg-pp-surface shadow-pp transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex h-full flex-col">
          <div className="flex items-start justify-between gap-3 border-b border-pp-line px-4 py-4">
            <div>
              <h2 id="report-issue-title" className="text-base font-semibold tracking-tight text-pp-ink">
                Report a bug or feature
              </h2>
              <p className="mt-1 text-xs text-pp-muted">
                Tell us what you were doing, what happened, or what you think should work differently.
                We'll include this screen and your current settings automatically.
              </p>
            </div>
            <button
              type="button"
              onClick={close}
              className="shrink-0 rounded-lg p-1 text-pp-muted hover:bg-pp-paper"
              title="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-4">
            {sent ? (
              <p className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
                Thanks — your report was sent.
              </p>
            ) : (
              <>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={8}
                  placeholder="What were you trying to do? What happened instead? Or: what feature would help?"
                  className="w-full rounded-lg border border-pp-line bg-pp-surface p-2.5 text-sm"
                />
                {error && <p className="mt-2 text-sm text-rose-700">{error}</p>}
              </>
            )}
          </div>

          <div className="flex justify-end gap-2 border-t border-pp-line px-4 py-3">
            <button
              type="button"
              onClick={close}
              className="rounded-xl border border-pp-line px-4 py-2 text-sm font-medium text-pp-muted hover:bg-pp-paper"
            >
              {sent ? 'Close' : 'Cancel'}
            </button>
            {!sent && (
              <button
                type="button"
                disabled={!description.trim() || submitting}
                onClick={onSubmit}
                className="rounded-xl bg-rose-600 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-700 disabled:opacity-50"
              >
                {submitting ? 'Sending…' : 'Send report'}
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
